import html2canvas from 'html2canvas';

/**
 * Turns the hidden receipt DOM into the bitmap the native shell sends to the printer.
 *
 * html2canvas does not rasterize the node it is handed: it clones the whole document into a
 * fresh iframe and measures the clone. `<style>` elements survive that clone — html2canvas
 * copies their `cssRules` across — but a `<link rel="stylesheet">` is cloned as a plain node,
 * so the iframe re-downloads the stylesheet on every print. In production that link is Vite's
 * single hashed bundle (`/assets/index-<hash>.css`), which is where all of Tailwind lives, so
 * a print is only as good as that re-fetch. When it fails the receipt is measured with
 * user-agent defaults: every `flex justify-between` row collapses to a block (prices drop
 * onto the line below their item), section borders and the whole type scale disappear — and
 * nothing throws, because a failed `<link>` does not stop the clone's `load` event.
 *
 * It fails more often than it sounds. A terminal keeps one session alive for days, so after a
 * deploy it asks for a hash the current deployment no longer has, and `vercel.json`'s
 * catch-all rewrite answers `index.html` with a 200 — which the WebView refuses to apply as a
 * stylesheet. Until the app is reloaded, every receipt prints unstyled. A cache eviction plus
 * a moment offline does the same thing.
 *
 * So the clone is handed the rules we already hold in memory instead, and the result is
 * checked before it reaches the printer.
 */

const CAPTURE_TIMEOUT = 2000;
const CAPTURE_POLL_INTERVAL = 16;

// Properties on the receipt root that only the app stylesheet can set (`fixed`, and the
// `pt-[50px]`/`pb-[130px]` bleed). They are read off the live element rather than hardcoded,
// so the probe follows the receipt if those classes ever change — and where nothing is styled
// at all, both sides compute the same defaults and the check simply stays quiet.
const PROBED_PROPERTIES = ['position', 'padding-top', 'padding-bottom'];

const cssTextByHref = new Map();

function resolveHref(href, base) {
  try {
    return new URL(href, base).href;
  } catch {
    return href ?? '';
  }
}

function readCssText(sheet) {
  try {
    return Array.from(sheet.cssRules ?? [])
      .map((rule) => rule.cssText)
      .join('\n');
  } catch {
    // `cssRules` throws a SecurityError on a cross-origin sheet — the Google Fonts link. There
    // is nothing to inline for those: they stay in the clone and take their chances.
    return '';
  }
}

function cacheCssText(sourceDocument) {
  Array.from(sourceDocument.styleSheets).forEach((sheet) => {
    const href = sheet.href && resolveHref(sheet.href, sourceDocument.baseURI);

    if (!href || cssTextByHref.has(href)) {
      return;
    }

    const cssText = readCssText(sheet);

    if (cssText) {
      cssTextByHref.set(href, cssText);
    }
  });
}

/**
 * Replaces every `<link rel="stylesheet">` in the clone with a `<style>` holding that sheet's
 * rules, read from the live document — same-origin, so `cssRules` is readable — which takes
 * the network out of the print path entirely.
 */
export function inlineStyleSheets(clonedDocument, sourceDocument = document) {
  const links = Array.from(clonedDocument.querySelectorAll('link[rel~="stylesheet"][href]'));

  if (!links.length) {
    return;
  }

  cacheCssText(sourceDocument);

  links.forEach((link) => {
    const cssText = cssTextByHref.get(resolveHref(link.getAttribute('href'), sourceDocument.baseURI));

    if (!cssText) {
      return;
    }

    const style = clonedDocument.createElement('style');
    style.textContent = cssText;
    // Replaced in place rather than appended: the bundle has to keep its cascade position
    // ahead of the <style> tags antd's CSS-in-JS appends after it at runtime.
    link.replaceWith(style);
  });
}

/**
 * Reads the probe off the receipt while it is still on screen, which is the only moment it can
 * be trusted: html2canvas holds the element for the whole capture — clone, iframe load,
 * `fonts.ready` — and the receipt is unmounted the instant any print clears the slot. A
 * detached element computes to nothing at all in Blink, so re-reading it later made every
 * property "mismatch" against an empty string and threw away a receipt whose clone was
 * perfectly styled. The clone is a snapshot of the DOM as it was when the capture started, so
 * that is what it has to be compared against.
 */
export function readStyleProbe(element) {
  const view = element?.ownerDocument?.defaultView;

  if (!view) {
    return null;
  }

  const style = view.getComputedStyle(element);
  const probe = PROBED_PROPERTIES.reduce(
    (values, property) => ({ ...values, [property]: style.getPropertyValue(property) }),
    {},
  );

  // Nothing computed at all: in Blink that means the element is already detached. There is no
  // reference left to compare the clone against, and calling that a lost stylesheet is a lie.
  return Object.values(probe).some(Boolean) ? probe : null;
}

/**
 * Last line of defence: if the clone computes differently from the receipt as it was when the
 * capture started, the stylesheet did not make it in and the bitmap would be an unreadable
 * receipt. Better to fail the print — the caller toasts, and Sentry gets a reason — than to
 * print it.
 */
export function assertClonedReceiptIsStyled(probe, clonedElement) {
  const clonedView = clonedElement?.ownerDocument?.defaultView;

  if (!probe || !clonedView) {
    return;
  }

  const actual = clonedView.getComputedStyle(clonedElement);

  const mismatches = Object.entries(probe)
    .filter(([property, value]) => actual.getPropertyValue(property) !== value)
    .map(([property, value]) => `${property}: ${actual.getPropertyValue(property)} instead of ${value}`);

  if (mismatches.length) {
    throw new Error(`Receipt clone lost the app stylesheet (${mismatches.join(', ')})`);
  }
}

/**
 * Resolves with the receipt node once React has committed it. Polls instead of waiting out a
 * fixed delay: the receipt mounts only for the print that needs it, and on a loaded terminal
 * it can miss a fixed window — which used to leave the capture with a null node.
 */
export function waitForReceiptElement(elementId, timeout = CAPTURE_TIMEOUT) {
  return new Promise((resolve, reject) => {
    const deadline = Date.now() + timeout;

    const poll = () => {
      const element = document.getElementById(elementId);

      if (element) {
        resolve(element);

        return;
      }

      if (Date.now() >= deadline) {
        reject(new Error(`Receipt #${elementId} did not render within ${timeout}ms`));

        return;
      }

      setTimeout(poll, CAPTURE_POLL_INTERVAL);
    };

    poll();
  });
}

export default async function captureReceipt(element) {
  const probe = readStyleProbe(element);

  const canvas = await html2canvas(element, {
    // scale: 1 keeps the bitmap 1px-per-dot, which is what makes usePaperWidth's CSS pixels
    // equal printer dots on a device with devicePixelRatio > 1.
    scale: 1,
    onclone: (clonedDocument, clonedElement) => {
      inlineStyleSheets(clonedDocument);
      assertClonedReceiptIsStyled(probe, clonedElement);
    },
  });

  return canvas.toDataURL();
}
